import {
  type MutableRefObject,
  useCallback,
  useLayoutEffect,
  useRef,
} from 'react';

import { useMediaQuery, useTheme } from '@mui/material';

import {
  ANIMATIONS,
  DIMENSIONS,
} from '@modules/_shared/components/report-editor/constants';
import { type PageId } from '@modules/_shared/components/report-editor/ReportEditorContext';
import { type ReportEditorPerformanceTracker } from '@modules/_shared/components/report-editor/useReportEditorPerformance';

const MAX_POLL_ATTEMPTS = 30; // 3 seconds at 100ms intervals

const renderAndWaitForPagedJs = (
  iframe: HTMLIFrameElement | null,
  html: string | null,
  onReady: () => void,
  crmPreview: boolean,
) => {
  if (!iframe || html == null || !iframe.contentDocument) {
    return;
  }

  const doc = iframe.contentDocument;
  const win = iframe.contentWindow as any;

  doc.open();
  doc.write(
    crmPreview
      ? html.replace(/<html(?=[\s>])/i, '<html data-crm-preview="true"')
      : html,
  );
  doc.close();

  win.addEventListener('pagedjs:rendering:complete', () => onReady(), {
    once: true,
  });
};

export const scaleIframeBody = (iFrameRef: HTMLIFrameElement | null) => {
  if (!iFrameRef) {
    return;
  }

  const iFrameDoc = iFrameRef.contentDocument;
  if (!iFrameDoc || !iFrameDoc.body) {
    return;
  }

  const frame = iFrameDoc.body;
  const containerWidth = iFrameRef.getBoundingClientRect().width;
  const availableWidth = containerWidth - 2 * DIMENSIONS.SIDE_MARGIN;
  const pageWidth = DIMENSIONS.PAGE_WIDTH;

  // Only scale down when container is smaller than page width, never scale up
  const scale = availableWidth < pageWidth ? availableWidth / pageWidth : 1;

  requestAnimationFrame(() => {
    Object.assign(frame.style, {
      transformOrigin: 'top left',
      position: 'absolute',
      transform:
        scale < 1 ? `scale(${scale}) translate(-50%, 0)` : 'translate(-50%, 0)',
      left: '50%',
      margin: '0',
    });
  });
};

const scrollElementIntoView = (
  element: HTMLElement,
  isDesktop: boolean,
  behavior: ScrollBehavior,
) => {
  if (!element) {
    return;
  }

  const page = element.closest('.pagedjs_page') as HTMLElement;
  const targetElement = page || element;

  const iframeWindow = targetElement.ownerDocument.defaultView;
  if (!iframeWindow) {
    return;
  }

  const rect = targetElement.getBoundingClientRect();
  const targetTop = iframeWindow.scrollY + rect.top;
  const top = isDesktop
    ? targetTop
    : targetTop - Math.max(0, (iframeWindow.innerHeight - rect.height) / 2);

  iframeWindow.scrollTo({ top, behavior });
};

const createBufferIframe = () => {
  const iframe = document.createElement('iframe');
  Object.assign(iframe.style, {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    border: 'none',
    pointerEvents: 'none',
    opacity: '0',
    transition: `opacity ${ANIMATIONS.CROSSFADE_MS}ms ease-in-out`,
  });
  return iframe;
};

// Polls scrollToPage until the target element is found or max attempts reached
const pollScrollToPage = (
  pageId: string,
  scrollFn: (id: string) => boolean,
): (() => void) => {
  let attempts = 0;
  const intervalId = window.setInterval(() => {
    attempts += 1;
    if (scrollFn(pageId) || attempts >= MAX_POLL_ATTEMPTS) {
      clearInterval(intervalId);
    }
  }, 100);
  return () => clearInterval(intervalId);
};

const useResponsiveIframeRenderer = (
  iFrameRef: HTMLIFrameElement | null,
  markup: string | null,
  viewerPageId?: PageId,
  scrollToPageRef?: MutableRefObject<(pageId: PageId) => void>,
  crmPreview = false,
  performanceTracker?: ReportEditorPerformanceTracker,
): MutableRefObject<HTMLIFrameElement | null> => {
  const { breakpoints } = useTheme();
  const isDesktop = useMediaQuery(breakpoints.up('md'));

  const bufferIframeRef = useRef<HTMLIFrameElement | null>(null);
  const activeIframeRef = useRef<HTMLIFrameElement | null>(null);
  const laidOutIframesRef = useRef(new WeakSet<HTMLIFrameElement>());
  const initialScrollRef = useRef<() => void>(() => {});

  // Stable ref callback — reads activeIframeRef.current on each call
  const scrollToPage = useCallback(
    (targetPageId: string, behavior: ScrollBehavior = 'smooth'): boolean => {
      const currentIframe = activeIframeRef.current;
      if (
        !currentIframe ||
        !currentIframe.contentDocument ||
        !laidOutIframesRef.current.has(currentIframe)
      ) {
        return false;
      }

      const targetElement =
        currentIframe.contentDocument.getElementById(targetPageId);

      if (targetElement) {
        scrollElementIntoView(targetElement, isDesktop, behavior);
        return true;
      }

      return false;
    },
    [isDesktop],
  );

  useLayoutEffect(() => {
    initialScrollRef.current = () => {
      if (isDesktop && viewerPageId) {
        scrollToPage(viewerPageId, 'instant');
      }
    };
  }, [isDesktop, viewerPageId, scrollToPage]);

  // Expose scrollToPage to the parent via ref
  useLayoutEffect(() => {
    if (scrollToPageRef) {
      scrollToPageRef.current = (pageId: string) => {
        scrollToPage(pageId);
      };
    }
  }, [scrollToPage, scrollToPageRef]);

  useLayoutEffect(() => {
    const container = iFrameRef?.parentElement;

    if (!container || !iFrameRef || markup == null) {
      return;
    }

    // Initialize on first render
    if (!activeIframeRef.current) {
      activeIframeRef.current = iFrameRef;
      iFrameRef.style.display = 'block';
      performanceTracker?.cancelEditTracking();

      renderAndWaitForPagedJs(
        iFrameRef,
        markup,
        () => {
          scaleIframeBody(iFrameRef);
          requestAnimationFrame(() => {
            laidOutIframesRef.current.add(iFrameRef);
            initialScrollRef.current();
          });
        },
        crmPreview,
      );
      return;
    }

    // Always create a fresh buffer iframe to avoid Paged.js/Tailwind state leaks
    // (reusing iframes causes progressive degradation as window globals accumulate)
    bufferIframeRef.current?.remove();
    const bufferIframe = createBufferIframe();
    bufferIframeRef.current = bufferIframe;
    container.appendChild(bufferIframe);

    const activeIframe = activeIframeRef.current;

    // Track whether this effect has been superseded by a newer markup change
    let aborted = false;

    // Force full render mode for buffer iframes — lazy mode uses
    // IntersectionObserver which won't fire for hidden/off-screen sections
    renderAndWaitForPagedJs(
      bufferIframe,
      markup,
      () => {
        if (aborted) {
          bufferIframe.remove();
          return;
        }

        performanceTracker?.onIframeRenderComplete();
        scaleIframeBody(bufferIframe);
        laidOutIframesRef.current.add(bufferIframe);

        // Preserve scroll position from the old iframe
        const oldScrollTop = activeIframe.contentWindow?.scrollY ?? 0;
        bufferIframe.contentWindow?.scrollTo({
          top: oldScrollTop,
          behavior: 'instant',
        });

        // Enable interaction on buffer, disable on old
        bufferIframe.style.pointerEvents = 'auto';
        activeIframe.style.pointerEvents = 'none';

        // Crossfade on next frame (needed for CSS transition to trigger)
        requestAnimationFrame(() => {
          bufferIframe.style.opacity = '1';
          activeIframe.style.opacity = '0';
        });

        // New active is the fresh buffer
        activeIframeRef.current = bufferIframe;
        bufferIframeRef.current = null;
        performanceTracker?.onIframeSwapComplete();

        // Destroy old iframe after crossfade completes
        setTimeout(() => {
          if (activeIframe !== iFrameRef) {
            activeIframe.remove();
          } else {
            activeIframe.style.display = 'none';
          }
        }, ANIMATIONS.CROSSFADE_MS + 50);
      },
      crmPreview,
    );

    return () => {
      aborted = true;
    };
  }, [markup, iFrameRef, crmPreview, performanceTracker]);

  // Scroll to the active page when the user clicks a step (viewerPageId changes)
  useLayoutEffect(() => {
    if (isDesktop && viewerPageId) {
      return pollScrollToPage(viewerPageId, scrollToPage);
    }
  }, [viewerPageId, scrollToPage, isDesktop]);

  // Mobile: scroll when the viewer tab becomes visible after a step click
  useLayoutEffect(() => {
    const container = iFrameRef?.parentElement;
    if (isDesktop || !container || !viewerPageId) {
      return;
    }

    let cleanupPoll: (() => void) | null = null;

    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;

      cleanupPoll?.();
      cleanupPoll = null;

      if (entry.isIntersecting) {
        cleanupPoll = pollScrollToPage(viewerPageId, scrollToPage);
      }
    };

    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 0.8,
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
      cleanupPoll?.();
    };
  }, [viewerPageId, scrollToPage, isDesktop, iFrameRef]);

  useLayoutEffect(() => {
    const container = iFrameRef?.parentElement;
    if (!container) {
      return;
    }

    // Use ResizeObserver on container to scale active iframe
    const observer = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        const activeIframe = activeIframeRef.current;
        if (activeIframe) {
          scaleIframeBody(activeIframe);
        }
      });
    });

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [iFrameRef]);

  // Cleanup dynamically created iframes on unmount
  useLayoutEffect(() => {
    return () => {
      bufferIframeRef.current?.remove();
      if (
        activeIframeRef.current &&
        activeIframeRef.current !== iFrameRef &&
        activeIframeRef.current.parentElement
      ) {
        activeIframeRef.current.remove();
      }
    };
  }, [iFrameRef]);

  return activeIframeRef;
};

export default useResponsiveIframeRenderer;
